import { useMemo, useState } from 'react'

import { formatDecimal, formatInt, formatPct } from '../../lib/format.ts'
import type { FleetPayload, TenantVolume } from '../../lib/sse/types.ts'
import { Meter, utilisationTone } from '../primitives/Meter.tsx'
import { Panel } from '../primitives/Panel.tsx'
import { ReferenceStamp } from '../primitives/ReferenceStamp.tsx'
import { StatusDot } from '../primitives/StatusDot.tsx'
import { TABLE, TD, TD_NUM, TH, TH_BUTTON, TH_NUM, THEAD, TR } from '../primitives/table.ts'

type SortKey = 'events_per_sec' | 'events_per_endpoint' | 'quota_usage_pct' | 'endpoints'

const COLUMNS: ReadonlyArray<{ key: SortKey; label: string; title: string }> = [
  { key: 'events_per_sec', label: 'ev/s', title: 'Ordenar por volume absoluto' },
  { key: 'endpoints', label: 'endpoints', title: 'Ordenar por número de endpoints' },
  { key: 'events_per_endpoint', label: 'ev/endpoint', title: 'Ordenar por razão eventos/endpoint' },
  { key: 'quota_usage_pct', label: 'quota', title: 'Ordenar por uso da quota contratada' },
]

/** Multiple of the fleet median at which a ratio stops being a busy tenant. */
const OUTLIER_FACTOR = 2.5

/** Static map: Tailwind cannot see a class name assembled at runtime. */
const QUOTA_TEXT = {
  neutral: 'text-fg',
  accent: 'text-accent',
  ok: 'text-fg',
  warn: 'text-warn',
  crit: 'text-crit',
} as const

function median(values: readonly number[]): number {
  if (values.length === 0) return 0
  const sorted = [...values].sort((a, b) => a - b)
  const middle = Math.floor(sorted.length / 2)
  if (sorted.length % 2 === 1) return sorted[middle] ?? 0
  return ((sorted[middle - 1] ?? 0) + (sorted[middle] ?? 0)) / 2
}

interface Row extends TenantVolume {
  /** True when the server flagged it, or when the ratio is far off the median. */
  flagged: boolean
  /** How many times the fleet median this tenant's ratio is. */
  ratioFactor: number
}

/**
 * Volume by tenant.
 *
 * The ranking is by events per second because that is what capacity planning
 * asks for, but the *flag* is by events per endpoint, because those two
 * questions have different answers. A tenant with 418 hosts pushing 5.891 ev/s
 * is almost certainly an agent stuck in a loop; one with 2.940 hosts pushing
 * 6.104 ev/s is a large customer working normally. Sorting by raw volume puts
 * the second at the top and the first out of sight — which is precisely
 * backwards.
 *
 * The threshold is a multiple of the fleet *median* rather than the mean: one
 * looping tenant drags a mean upward and can mask itself.
 */
export function TenantsPanel({
  fleet,
  lastFleetAt,
  className,
}: {
  fleet: FleetPayload | null
  lastFleetAt: number | null
  className?: string
}) {
  // A tabela é alimentada pelo payload de referência, o mesmo que pode ficar
  // defasado quando o banco cai — por isso o painel recebe `fleet` inteiro, e
  // não só a lista: ele precisa poder dizer que o que está na tela envelheceu.
  const tenants = fleet?.tenants ?? []

  const [sort, setSort] = useState<SortKey>('events_per_sec')
  const [onlyFlagged, setOnlyFlagged] = useState(false)

  const { rows, flaggedCount, maxEps } = useMemo(() => {
    const centre = median(tenants.map((tenant) => tenant.events_per_endpoint))
    const limit = centre * OUTLIER_FACTOR

    const enriched: Row[] = tenants.map((tenant) => ({
      ...tenant,
      flagged: tenant.is_outlier || (centre > 0 && tenant.events_per_endpoint >= limit),
      ratioFactor: centre > 0 ? tenant.events_per_endpoint / centre : 0,
    }))

    const peak = enriched.reduce((max, tenant) => Math.max(max, tenant.events_per_sec), 0)

    return {
      rows: enriched,
      flaggedCount: enriched.filter((tenant) => tenant.flagged).length,
      maxEps: peak,
    }
  }, [tenants])

  const visible = useMemo(() => {
    const filtered = onlyFlagged ? rows.filter((row) => row.flagged) : rows
    // `quota_usage_pct` é nula para tenant sem quota contratada, e `null - null`
    // é NaN — que faz o comparador devolver NaN e a ordenação virar a ordem de
    // chegada. Sem quota vai para o fim da lista ordenada por quota.
    const value = (row: Row): number => row[sort] ?? -1
    return [...filtered].sort((a, b) => value(b) - value(a))
  }, [rows, sort, onlyFlagged])

  return (
    <Panel
      title="Volume por tenant"
      className={className}
      flush
      meta={
        <>
          {/* Só aparece quando há o que avisar: a linha de meta já carrega o
              critério e o filtro, e um carimbo de frescor permanente aqui
              competiria com eles por atenção sem acrescentar nada. */}
          {fleet?.reference_stale === true && (
            <ReferenceStamp fleet={fleet} lastFleetAt={lastFleetAt} />
          )}
          <button
            type="button"
            aria-pressed={onlyFlagged}
            onClick={() => setOnlyFlagged((previous) => !previous)}
            className={`border px-1.5 py-0.5 text-micro uppercase tracking-label ${
              onlyFlagged
                ? 'border-crit text-crit'
                : 'border-line-strong text-fg-muted hover:bg-inset hover:text-fg'
            }`}
          >
            <span className="tnum">{formatInt(flaggedCount)}</span> outlier
            {flaggedCount === 1 ? '' : 's'}
          </button>
        </>
      }
    >
      <div className="h-full overflow-y-auto">
        <table className={TABLE}>
          <caption className="sr-only">
            Tenants ordenados por {sort}.
          </caption>
          <thead className={THEAD}>
            <tr>
              <th scope="col" className={TH}>
                Tenant
              </th>
              {COLUMNS.map((column) => (
                <th
                  key={column.key}
                  scope="col"
                  className={TH_NUM}
                  aria-sort={sort === column.key ? 'descending' : 'none'}
                >
                  <button
                    type="button"
                    title={column.title}
                    onClick={() => setSort(column.key)}
                    className={`${TH_BUTTON} justify-end ${sort === column.key ? 'text-fg' : ''}`}
                  >
                    {column.label}
                    {sort === column.key && (
                      <span aria-hidden="true" className="text-accent">
                        ↓
                      </span>
                    )}
                  </button>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {visible.map((tenant) => {
              const quotaTone = utilisationTone(tenant.quota_usage_pct ?? 0, 75, 90)
              return (
                <tr key={tenant.tenant_id} className={TR}>
                  <th scope="row" className={`${TD} max-w-0 font-normal`}>
                    <span className="flex items-center gap-1.5">
                      {tenant.flagged && <StatusDot tone="crit" />}
                      <span className={`truncate ${tenant.flagged ? 'text-fg' : ''}`}>
                        {tenant.name}
                      </span>
                    </span>
                  </th>

                  <td className={TD_NUM}>
                    <span className="flex items-center justify-end gap-2">
                      {/* Share of the busiest tenant — context without a second column. */}
                      <span aria-hidden="true" className="h-1 w-10 shrink-0 bg-inset">
                        <span
                          className="block h-full bg-seq-3"
                          style={{ width: `${maxEps > 0 ? (tenant.events_per_sec / maxEps) * 100 : 0}%` }}
                        />
                      </span>
                      {formatInt(tenant.events_per_sec)}
                    </span>
                  </td>

                  <td className={TD_NUM}>{formatInt(tenant.endpoints)}</td>

                  <td className={`${TD_NUM} ${tenant.flagged ? 'text-crit' : ''}`}>
                    {formatDecimal(tenant.events_per_endpoint, 2)}
                    <span className="ml-1.5 text-micro text-fg-muted">
                      {tenant.ratioFactor > 0 ? `${formatDecimal(tenant.ratioFactor, 1)}×` : ''}
                    </span>
                  </td>

                  {/* Sem quota contratada não há teto para desenhar, e inventar
                      um (uma barra vazia, um 0%) diria que o tenant está longe
                      de um limite que não existe. A célula assume isso em
                      palavras e some com a barra. */}
                  <td className={TD_NUM}>
                    {tenant.quota_usage_pct === null ? (
                      <span className="text-micro uppercase tracking-label text-fg-muted">
                        sem quota
                      </span>
                    ) : (
                      <span className="flex items-center justify-end gap-2">
                        <span className="w-10 shrink-0">
                          <Meter
                            pct={tenant.quota_usage_pct}
                            tone={quotaTone}
                            size="sm"
                            label={`Uso de quota de ${tenant.name}`}
                            valueText={`${formatInt(tenant.events_per_sec)} de ${formatInt(tenant.quota_events_per_sec)} ev/s contratados`}
                          />
                        </span>
                        <span className={QUOTA_TEXT[quotaTone]}>
                          {formatPct(tenant.quota_usage_pct, 0)}
                        </span>
                      </span>
                    )}
                  </td>
                </tr>
              )
            })}
            {visible.length === 0 && (
              <tr>
                <td colSpan={5} className={`${TD} text-fg-muted`}>
                  {onlyFlagged
                    ? 'Nenhum tenant fora da faixa esperada de eventos por endpoint.'
                    : 'Sem dados de tenant na última leitura da frota.'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </Panel>
  )
}
